import { createContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import MailIcon from '@mui/icons-material/Mail';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import StoreIcon from '@mui/icons-material/Store';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import ImportantDevicesIcon from '@mui/icons-material/ImportantDevices';
import LogoutIcon from '@mui/icons-material/Logout';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ImageIcon from '@mui/icons-material/Image';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonIcon from '@mui/icons-material/Person';
import AttractionsIcon from '@mui/icons-material/Attractions';
import QrCodeIcon from '@mui/icons-material/QrCode';
import WebIcon from '@mui/icons-material/Web';
import PolicyIcon from '@mui/icons-material/Policy';

import {
  home as homePath,
  jobs as jobsPath,
  customers as customersPath,
  moments as momentsPath,
  jobConfigs as jobConfigsPath,
  jobConfigTemplates as jobConfigTemplatesPath,
  members as membersPath,
  companyProfile as companyProfilePath,
  contentProviders as contentProvidersPath,
  configureApps as configureAppsPath,
  userProfile as userProfilePath,
  kiosks as kiosksPath,
  marketing as marketingPath,
  purchases as purchasesPath,
  reports as reportsPath,
  uploads as uploadsPath,
  billing as billingPath,
  accountBillings as accountBillingsPath,
  accountPricings as accountPricingsPath,
  billingReport as billingReportPath,
  billingExportSchedules as billingExportSchedulesPath,
  salesReport as salesReportPath,
  analyticsReport as analyticsReportPath,
  photographerHourlyReport as photographerHourlyReportPath,
  accounts as accountsPath,
  catalogs as catalogsPath,
  qrCodeGenerator as qrCodeGeneratorPath,
  guests as guestsPath,
  accessCodePages as accessCodePagesPath,
  gdpr as gdprPath,
  dailyReadReport as dailyReadReportPath,
  adminDailyReadReport as adminDailyReadReportPath,
  workerStatusReport as workerStatusReportPath
} from 'app/routes';

import {
  logout
} from 'shared/actions/userActions';

import {
  setDarkMode,
  switchAccount
} from 'app/actions/portalActions';

import {
  SUPER_USER_ACCOUNT_ID
} from 'app/utils/Constants';

const useNav = () => {
  const isDarkMode = useSelector(state => state.portalReducer.darkMode);
  const account = useSelector(state => state.portalReducer.account);
  const user = useSelector(state => state.userReducer.user);
  const dispatch = useDispatch();
  const history = useHistory();

  const switchToWheel = async () => {
    await dispatch(switchAccount(SUPER_USER_ACCOUNT_ID));
    history.push(homePath);
  }

  const accounts = { label: 'Accounts', path: accountsPath, icon: <PeopleIcon /> };
  const accountBillings = { label: 'Account Billing', path: accountBillingsPath, icon: <AccountBalanceIcon /> };
  const accountPricings = { label: 'Account Pricing', path: accountPricingsPath, icon: <AccountTreeIcon /> };
  const analyticsReport = { label: 'Analytics Report', path: analyticsReportPath, icon: <AnalyticsIcon /> };
  const billings = { label: 'Billing Entities', path: billingPath, icon: <StoreIcon /> };
  const billingReport = { label: 'Billing Report', path: billingReportPath, icon: <SummarizeIcon /> };
  const dailyReadReport = { label: 'Daily Read Report', path: dailyReadReportPath, icon: <SummarizeIcon /> };
  const adminDailyReadReport = { label: 'Daily Read Report', path: adminDailyReadReportPath, icon: <SummarizeIcon /> };
  const workerStatusReport = { label: 'Worker Status Report', path: workerStatusReportPath, icon: <SummarizeIcon /> };
  const billingExportSchedules = { label: 'Billing Export Schedules', path: billingExportSchedulesPath, icon: <AccessTimeIcon /> };
  const catalogs = { label: 'Catalogs', path: catalogsPath, icon: <CollectionsBookmarkIcon /> };
  const customers = { label: 'Customers', path: customersPath, icon: <PeopleIcon /> };
  const companyProfile = { label: 'Company Profile', path: companyProfilePath, icon: <BusinessIcon /> };
  const configureApps = { label: 'Configure Apps', path: configureAppsPath, icon: <BuildIcon /> };
  const contentProviders = { label: 'Content Providers', path: contentProvidersPath, icon: <AddBusinessIcon /> };
  const darkMode = { label: 'Dark Mode', onClick: () => dispatch(setDarkMode(true)), icon: <AutoFixHighIcon /> };
  const guests = { label: 'Guests', path: guestsPath, icon: <PeopleIcon /> };
  const jobConfigs = { label: 'Products', path: jobConfigsPath, icon: <ProductionQuantityLimitsIcon /> };
  const jobConfigTemplates = { label: 'Marketplace', path: jobConfigTemplatesPath, icon: <ProductionQuantityLimitsIcon /> };
  const jobs = { label: 'Jobs', path: jobsPath, icon: <CollectionsBookmarkIcon /> };
  const kiosks = { label: 'Kiosks', path: kiosksPath, icon: <ImportantDevicesIcon /> };
  const lightMode = { label: 'Light Mode', onClick: () => dispatch(setDarkMode(false)), icon: <AutoFixHighIcon /> };
  const logoutLink = { label: 'Logout', onClick: () => dispatch(logout()), icon: <LogoutIcon /> };
  const marketing = { label: 'Marketing', path: marketingPath, icon: <MailIcon /> };
  const members = { label: 'Members', path: membersPath, icon: <AssignmentIndIcon /> };
  const moments = { label: 'Moments', path: momentsPath, icon: <ImageIcon /> };
  const purchases = { label: 'Purchases', path: purchasesPath, icon: <LocalAtmIcon /> };
  const photographerHourlyReport = { label: 'Photographer Hourly Report', path: photographerHourlyReportPath, icon: <CameraAltIcon /> };
  const qrCodeGenerator = { label: 'QR Codes', path: qrCodeGeneratorPath, icon: <QrCodeIcon /> };
  const salesReport = { label: 'Sales Report', path: salesReportPath, icon: <SummarizeIcon /> };
  const uploads = { label: 'Uploads', path: uploadsPath, icon: <CloudUploadIcon /> };
  const userProfile = { label: 'User Profile', path: userProfilePath, icon: <PersonIcon /> };
  const wheel = { label: 'Wheel', onClick: switchToWheel, icon: <AttractionsIcon /> };
  const accessCodePages = { label: 'Access Code Pages', path: accessCodePagesPath, icon: <WebIcon /> };
  const gdpr = { label: 'GDPR', path: gdprPath, icon: <PolicyIcon /> };

  const getNavLinks = () => {
    if (account.id === SUPER_USER_ACCOUNT_ID) {
      return [
        accounts,
        accessCodePages,
        jobConfigTemplates,
        billings,
        accountBillings,
        accountPricings,
        billingExportSchedules,
        gdpr
      ];
    }

    if (account.feature_flags.club_med) {
      return [
        guests,
        catalogs,
        moments,
        kiosks,
        jobConfigs,
        marketing
      ];

    }

    if (account.feature_flags.kiosk_mode) {
      let navLinks = [
        kiosks
      ];

      if (account.kiosk_catalog_screen) {
        navLinks.push(catalogs);
      }

      navLinks.push(customers);

      if (account.feature_flags.qr_code_generator) {
        navLinks.push(qrCodeGenerator);
      }

      return navLinks.concat([
        uploads,
        moments,
        jobConfigs,
        marketing
      ]);
    }

    let navLinks = [
      jobs,
      customers
    ];

    if (account.feature_flags.qr_code_generator) {
      navLinks.push(qrCodeGenerator);
    }

    return navLinks.concat([
      moments,
      kiosks,
      jobConfigs,
      marketing,
      uploads
    ]);
  }

  const getReportMenuLinks = () => {
    if (account.id === SUPER_USER_ACCOUNT_ID) {
      return [
        billingReport,
        adminDailyReadReport,
        workerStatusReport
      ]
    }

    return [
      purchases,
      salesReport,
      // analyticsReport,
      photographerHourlyReport,
      dailyReadReport
    ]
  }

  const getAccountMenuLinks = () => {
    if (account.id === SUPER_USER_ACCOUNT_ID) {
      return [
        userProfile,
        logoutLink
      ]
    }

    let links = [];

    if (user.has_wheel) {
      links.push(wheel);
    }

    return links.concat([
      companyProfile,
      members,
      contentProviders,
      configureApps,
      userProfile,
      logoutLink
    ]);
  }

  const getUserMenuLinks = () => {
    let links = [
      userProfile
    ];

    if (isDarkMode) {
      links.push(lightMode);
    } else {
      links.push(darkMode);
    }

    links.push(logoutLink);

    return links;
  }

  return {
    getNavLinks,
    getReportMenuLinks,
    getAccountMenuLinks,
    getUserMenuLinks
  }
}

export default useNav;
